import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Youtube, Plus, Trash2, Loader, Link2, ExternalLink, Pencil } from 'lucide-react';

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];

export default function AdminYouTubePage() {
  const [activeTab, setActiveTab] = useState('entries');
  const [entries, setEntries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filterUser, setFilterUser] = useState('');
  const [userChannels, setUserChannels] = useState([]);
  const [form, setForm] = useState({
    user_id: '', period_month: new Date().getMonth() + 1,
    period_year: new Date().getFullYear(), gross_amount: '', youtube_channel_id: '', notes: ''
  });
  const [channelForm, setChannelForm] = useState({ user_id: '', channel_url: '', channel_name: '', notes: '' });
  const [savingChannel, setSavingChannel] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [e, ch, u] = await Promise.all([
        api.get(`/youtube-adsense/admin/all${filterUser ? `?user_id=${filterUser}` : ''}`).catch(() => ({ data: { entries: [] } })),
        api.get('/youtube-adsense/channels/all').catch(() => ({ data: { channels: [] } })),
        api.get('/admin/users?limit=500')
      ]);
      setEntries(e.data.entries || []);
      setChannels(ch.data.channels || []);
      setUsers((u.data.users || []).filter(u => u.role !== 'admin'));
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, [filterUser]);

  // When user changes in add form, load their channels
  useEffect(() => {
    if (form.user_id) {
      api.get(`/youtube-adsense/channels/by-user/${form.user_id}`).then(r => setUserChannels(r.data.channels || [])).catch(() => setUserChannels([]));
    } else { setUserChannels([]); }
  }, [form.user_id]);

  const saveEntry = async () => {
    if (!form.user_id || !form.gross_amount) return toast.error('Seleziona utente e importo');
    setSaving(true);
    try {
      const { data } = await api.post('/youtube-adsense/admin', form);
      toast.success(`Salvato — Utente: €${data.user_share?.toFixed(2)} · PN: €${data.proudnoize_share?.toFixed(2)}`);
      setForm(f => ({ ...f, gross_amount: '', youtube_channel_id: '', notes: '' }));
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setSaving(false); }
  };

  const delEntry = async (id) => {
    if (!window.confirm('Eliminare?')) return;
    try { await api.delete(`/youtube-adsense/admin/${id}`); toast.success('Eliminato'); loadAll(); }
    catch (e) { toast.error('Errore'); }
  };

  const editEntry = (e) => {
    setForm({
      user_id: String(e.user_id),
      period_month: e.period_month,
      period_year: e.period_year,
      gross_amount: String(parseFloat(e.gross_amount)),
      youtube_channel_id: e.youtube_channel_id ? String(e.youtube_channel_id) : '',
      notes: e.notes || ''
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    toast('Modifica il guadagno e clicca Salva', { icon: '✏️' });
  };

  const saveChannel = async () => {
    if (!channelForm.user_id || !channelForm.channel_url) return toast.error('Utente e URL obbligatori');
    setSavingChannel(true);
    try {
      const { data } = await api.post('/youtube-adsense/channels', channelForm);
      toast.success(`Canale "${data.channel?.channel_name}" aggiunto!`);
      setChannelForm({ user_id: '', channel_url: '', channel_name: '', notes: '' });
      loadAll();
    } catch (err) { toast.error(err.response?.data?.error || 'Errore'); }
    finally { setSavingChannel(false); }
  };

  const delChannel = async (id) => {
    if (!window.confirm('Eliminare questo canale?')) return;
    try { await api.delete(`/youtube-adsense/channels/${id}`); toast.success('Eliminato'); loadAll(); }
    catch (e) { toast.error('Errore'); }
  };

  const tabs = [
    { id: 'entries', label: `Guadagni (${entries.length})`, icon: '💰' },
    { id: 'channels', label: `Canali (${channels.length})`, icon: '📺' },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-6 flex items-center gap-2"><Youtube size={24} className="text-red-500" />YouTube AdSense</h1>
      <div className="flex gap-2 mb-6">
        {tabs.map(t => (<button key={t.id} onClick={() => setActiveTab(t.id)} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === t.id ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-gray-800 text-gray-400 border border-gray-700'}`}>{t.icon} {t.label}</button>))}
      </div>

      {/* TAB: Guadagni */}
      {activeTab === 'entries' && (<>
        <div className="card-dark p-6 mb-6">
          <h2 className="font-bold text-white mb-4">Aggiungi Guadagno YouTube</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Utente *</label>
              <select className="input-field-dark" value={form.user_id} onChange={e => setForm(f => ({ ...f, user_id: e.target.value, youtube_channel_id: '' }))}>
                <option value="">Seleziona...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Mese</label>
              <select className="input-field-dark" value={form.period_month} onChange={e => setForm(f => ({ ...f, period_month: parseInt(e.target.value) }))}>
                {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Anno</label>
              <input type="number" className="input-field-dark" value={form.period_year} onChange={e => setForm(f => ({ ...f, period_year: parseInt(e.target.value) }))} min={2020} max={2030} />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Importo Lordo (€) *</label>
              <input type="number" step="0.01" className="input-field-dark" value={form.gross_amount} onChange={e => setForm(f => ({ ...f, gross_amount: e.target.value }))} placeholder="150.00" />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Canale</label>
              <select className="input-field-dark" value={form.youtube_channel_id} onChange={e => setForm(f => ({ ...f, youtube_channel_id: e.target.value }))}>
                <option value="">-- Nessun canale --</option>
                {userChannels.map(ch => <option key={ch.id} value={ch.id}>{ch.channel_name || ch.channel_url}</option>)}
              </select>
              {form.user_id && userChannels.length === 0 && <p className="text-[10px] text-yellow-400 mt-1">Nessun canale per questo utente. Aggiungine uno nel tab Canali.</p>}
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Note</label>
              <input className="input-field-dark" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opzionale..." />
            </div>
          </div>
          <button onClick={saveEntry} disabled={saving} className="btn-admin px-6 py-2.5"><Plus size={14} className="inline mr-1" />{saving ? '...' : (form.user_id && form.gross_amount ? 'Salva' : 'Aggiungi')}</button>
        </div>

        <div className="card-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-bold text-white">Storico</h2>
            <select className="input-field-dark w-56" value={filterUser} onChange={e => setFilterUser(e.target.value)}>
              <option value="">Tutti gli utenti</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
            </select>
          </div>
          {loading ? <Loader size={20} className="animate-spin text-red-400 mx-auto" /> : entries.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nessun guadagno YouTube</p>
          ) : (
            <div className="space-y-2">{entries.map(e => (
              <div key={e.id} className="bg-gray-900 rounded-lg p-4 flex items-center gap-4">
                <Youtube size={18} className="text-red-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{MONTHS[e.period_month - 1]} {e.period_year}</p>
                  <p className="text-xs text-gray-400">{e.first_name} {e.last_name} ({e.email}){e.channel_name ? ` · ${e.channel_name}` : ''}{e.notes ? ` · ${e.notes}` : ''}</p>
                </div>
                <div className="text-right"><p className="text-xs text-gray-500">Lordo</p><p className="text-white text-sm">€{parseFloat(e.gross_amount).toFixed(2)}</p></div>
                <div className="text-right"><p className="text-xs text-orange-500">PN</p><p className="text-orange-400 font-bold">€{parseFloat(e.proudnoize_share).toFixed(2)}</p></div>
                <div className="text-right"><p className="text-xs text-green-500">Utente</p><p className="text-green-400 font-bold">€{parseFloat(e.user_share).toFixed(2)}</p></div>
                <button onClick={() => editEntry(e)} className="p-1.5 text-gray-600 hover:text-yellow-400" title="Modifica"><Pencil size={14} /></button>
                <button onClick={() => delEntry(e.id)} className="p-1.5 text-gray-600 hover:text-red-500" title="Elimina"><Trash2 size={14} /></button>
              </div>
            ))}</div>
          )}
        </div>
      </>)}

      {/* TAB: Canali */}
      {activeTab === 'channels' && (<>
        <div className="card-dark p-6 mb-6">
          <h2 className="font-bold text-white mb-2">Aggiungi Canale YouTube</h2>
          <p className="text-xs text-gray-500 mb-4">Incolla l'URL del canale. Il nome viene estratto automaticamente.</p>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Utente *</label>
              <select className="input-field-dark" value={channelForm.user_id} onChange={e => setChannelForm(f => ({ ...f, user_id: e.target.value }))}>
                <option value="">Seleziona...</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">URL Canale *</label>
              <input className="input-field-dark" value={channelForm.channel_url} onChange={e => setChannelForm(f => ({ ...f, channel_url: e.target.value }))} placeholder="https://youtube.com/@nomcanale" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs text-gray-400 mb-1">Nome Canale (opzionale, auto-rilevato)</label>
              <input className="input-field-dark" value={channelForm.channel_name} onChange={e => setChannelForm(f => ({ ...f, channel_name: e.target.value }))} placeholder="Viene estratto dall'URL..." />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Note</label>
              <input className="input-field-dark" value={channelForm.notes} onChange={e => setChannelForm(f => ({ ...f, notes: e.target.value }))} placeholder="es. Canale principale, VEVO..." />
            </div>
          </div>
          <button onClick={saveChannel} disabled={savingChannel} className="btn-admin px-6 py-2.5"><Plus size={14} className="inline mr-1" />{savingChannel ? 'Salvataggio...' : 'Aggiungi Canale'}</button>
        </div>

        <div className="card-dark p-6">
          <h2 className="font-bold text-white mb-4">Canali Registrati ({channels.length})</h2>
          {channels.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Nessun canale registrato</p>
          ) : (
            <div className="space-y-2">{channels.map(ch => (
              <div key={ch.id} className="bg-gray-900 rounded-lg p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center flex-shrink-0"><Youtube size={18} /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium">{ch.channel_name || 'Senza nome'}</p>
                  <p className="text-xs text-gray-400">{ch.first_name} {ch.last_name} ({ch.email}){ch.notes ? ` · ${ch.notes}` : ''}</p>
                </div>
                <a href={ch.channel_url} target="_blank" rel="noreferrer" className="text-xs text-red-400 hover:underline flex items-center gap-1"><ExternalLink size={12} />Apri</a>
                <button onClick={() => delChannel(ch.id)} className="p-1.5 text-gray-600 hover:text-red-500"><Trash2 size={14} /></button>
              </div>
            ))}</div>
          )}
        </div>
      </>)}
    </div>
  );
}
